
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, TextField, SelectField, Alert } from '@dopomogai/ui';
import { Plus, Trash2, Shield } from 'lucide-react';
import { useSecrets } from '@dopomogai/agent-kit';
import { Link } from 'react-router-dom';

type SecretsMap = Record<string, string>; // ENV_VAR_NAME -> "${secret:NAME}"

export interface ToolSandboxEnvTabProps {
  value: SecretsMap | undefined;
  onChange: (next: SecretsMap) => void;
}

type Row = { envVar: string; secretName: string };

const tokenToName = (token: string | undefined | null): string => {
  if (!token) return '';
  const m = token.match(/^\$\{secret:([^}]+)\}$/i);
  return m ? m[1] : '';
};

const nameToToken = (name: string) => (name ? `\${secret:${name}}` : '');

export const ToolSandboxEnvTab: React.FC<ToolSandboxEnvTabProps> = ({ value, onChange }) => {
  const { secrets, isLoading, error } = useSecrets();

  const secretOptions = useMemo(
    () => (secrets || []).map(s => ({ value: s.name, label: s.name })),
    [secrets]
  );

  const [rows, setRows] = useState<Row[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const src = value || {};
    const initial: Row[] = Object.entries(src).map(([env, token]) => ({ envVar: env, secretName: tokenToName(token) }));
    setRows(initial);
  }, [value]);

  const addRow = () => setRows(prev => [...prev, { envVar: '', secretName: '' }]);

  const removeRow = (idx: number) => {
    const next = rows.slice();
    next.splice(idx, 1);
    setRows(next);
  };

  const updateRow = (idx: number, part: Partial<Row>) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...part };
    setRows(next);
  };

  const validate = (): string | null => {
    const seen = new Set<string>();
    for (const r of rows) {
      const key = (r.envVar || '').trim();
      if (!key) return 'Environment variable name is required.';
      if (!/^[A-Z0-9_]+$/.test(key)) return 'Env var names must be UPPER_SNAKE_CASE letters/numbers/underscores.';
      if (seen.has(key)) return `Duplicate env var: ${key}`;
      seen.add(key);
    }
    return null;
  };

  const save = () => {
    const e = validate();
    if (e) {
      setFormError(e);
      return;
    }
    setFormError(null);
    const next: SecretsMap = {};
    rows.forEach(r => {
      const env = r.envVar.trim();
      const token = nameToToken(r.secretName.trim());
      if (env && token) next[env] = token;
    });
    onChange(next);
  };

  return (
    <Card className="bg-white border-2 border-black" noPadding={false}>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-slate-700" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Sandbox Environment</p>
            <p className="text-xs text-slate-600">
              Map environment variables to secrets. Values are injected only inside the sandbox at runtime.
            </p>
            <p className="text-xs text-slate-600 mt-1">
              You can create and rotate secrets in the{' '}
              <Link to="/app/secrets" className="text-emerald-600 hover:underline">
                Secrets
              </Link>{' '}
              page.
            </p>
          </div>
        </div>

        {error && <Alert type="error" title="Failed to load secrets" message={error.message} />}
        {formError && <Alert type="error" message={formError} />}

        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <TextField
                  label="ENV var"
                  value={row.envVar}
                  onChange={(e) => updateRow(idx, { envVar: e.target.value })}
                  placeholder="API_TOKEN"
                />
              </div>
              <div className="col-span-6">
                <SelectField
                  label="Secret"
                  name={`secret-${idx}`}
                  value={row.secretName}
                  options={secretOptions}
                  onChange={(v) => updateRow(idx, { secretName: String(v) })}
                  placeholder={isLoading ? 'Loading secrets…' : 'Select secret'}
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} icon={<Trash2 className="w-4 h-4" />} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={addRow} icon={<Plus className="w-4 h-4" />}>
            Add mapping
          </Button>
          <Button variant="primary" size="sm" onClick={save}>Apply</Button>
        </div>

        <p className="text-xs text-slate-500">
          The tool’s code reads process.env.ENV_VAR_NAME at runtime. Secret values are never displayed in the UI.
        </p>
      </div>
    </Card>
  );
};

export default ToolSandboxEnvTab;
